(() => {
  'use strict';

  const API = window.AnushraV100 = window.AnushraV100 || {};
  const STORAGE_WISHLIST = 'anushra:wishlist:v100';
  const STORAGE_WISHLIST_LEGACY = 'anushra:wishlist:v2';
  const STORAGE_RECENT = 'anushra:recent:v100';
  const STORAGE_RECENT_LEGACY = 'anushra:recent:v2';
  const MAX_RECENT = 10;
  let globalBound = false;
  let cartSubscribed = false;

  const ready = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  };

  const safeParse = (value, fallback = []) => {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : fallback;
    } catch (_) {
      return fallback;
    }
  };

  const readList = (key, legacyKey = '') => {
    try {
      const current = safeParse(localStorage.getItem(key), []);
      if (current.length || !legacyKey) return current;
      const legacy = safeParse(localStorage.getItem(legacyKey), []);
      if (legacy.length) localStorage.setItem(key, JSON.stringify(legacy));
      return legacy;
    } catch (_) {
      return [];
    }
  };

  const writeList = (key, list) => {
    try { localStorage.setItem(key, JSON.stringify(list)); } catch (_) {}
  };

  const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  }[char]));

  const stopAction = (event) => {
    if (!event) return;
    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
  };

  const wishlistDrawer = () => document.querySelector('[data-anushra-wishlist]');
  const wishlistItems = () => readList(STORAGE_WISHLIST, STORAGE_WISHLIST_LEGACY);

  const updateWishlistUI = () => {
    const items = wishlistItems();
    const handles = new Set(items.map((item) => item.handle));

    document.querySelectorAll('[data-anushra-wishlist-button]').forEach((button) => {
      const active = handles.has(button.dataset.productHandle);
      button.setAttribute('aria-pressed', String(active));
      button.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Add to wishlist');
      const label = button.querySelector('.anushra-wishlist-button__label');
      if (label) label.textContent = active ? 'Saved to wishlist' : 'Add to wishlist';
    });

    document.querySelectorAll('[data-anushra-wishlist-count]').forEach((count) => {
      count.textContent = items.length;
      count.hidden = items.length === 0;
    });

    const drawer = wishlistDrawer();
    if (!drawer) return;
    const container = drawer.querySelector('[data-anushra-wishlist-items]');
    drawer.classList.toggle('is-empty', items.length === 0);
    if (!container) return;

    container.innerHTML = items.map((item) => `
      <article class="anushra-wishlist__item" data-wishlist-handle="${escapeHtml(item.handle)}">
        <a href="${escapeHtml(item.url)}" aria-label="View ${escapeHtml(item.title)}">
          ${item.image ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}" width="180" height="220" loading="lazy">` : ''}
        </a>
        <div>
          <a href="${escapeHtml(item.url)}"><span class="anushra-wishlist__item-title">${escapeHtml(item.title)}</span></a>
          <span class="anushra-wishlist__item-price">${escapeHtml(item.price)}</span>
        </div>
        <button type="button" class="anushra-wishlist__remove" data-anushra-wishlist-remove="${escapeHtml(item.handle)}" aria-label="Remove ${escapeHtml(item.title)}">×</button>
      </article>
    `).join('');
  };

  const openWishlist = () => {
    const drawer = wishlistDrawer();
    if (!drawer) return;
    updateWishlistUI();
    drawer.hidden = false;
    document.body.classList.add('anushra-drawer-open');
    requestAnimationFrame(() => drawer.querySelector('.anushra-wishlist__drawer')?.focus());
  };

  const closeWishlist = () => {
    const drawer = wishlistDrawer();
    if (!drawer) return;
    drawer.hidden = true;
    document.body.classList.remove('anushra-drawer-open');
  };

  const toggleWishlistProduct = (button) => {
    if (!button) return;
    const item = {
      handle: button.dataset.productHandle || '',
      title: button.dataset.productTitle || '',
      url: button.dataset.productUrl || '',
      image: button.dataset.productImage || '',
      price: button.dataset.productPrice || ''
    };
    if (!item.handle) return;

    const items = wishlistItems();
    const index = items.findIndex((entry) => entry.handle === item.handle);
    if (index >= 0) items.splice(index, 1);
    else items.unshift(item);
    writeList(STORAGE_WISHLIST, items.slice(0, 100));
    updateWishlistUI();
  };

  API.wishlist = { open: openWishlist, close: closeWishlist, toggle: toggleWishlistProduct, refresh: updateWishlistUI };

  window.anushraWishlistProductClick = (event, button) => {
    stopAction(event);
    toggleWishlistProduct(button);
    return false;
  };

  window.anushraWishlistOpenClick = (event) => {
    stopAction(event);
    openWishlist();
    return false;
  };

  const setupGlobalEvents = () => {
    if (globalBound) return;
    globalBound = true;

    /* Capture before Dawn's stretched product-card links. */
    document.addEventListener('click', (event) => {
      const productButton = event.target.closest?.('[data-anushra-wishlist-button]');
      if (productButton) {
        stopAction(event);
        toggleWishlistProduct(productButton);
        return;
      }

      const wishlistToggle = event.target.closest?.('[data-anushra-wishlist-toggle]');
      if (wishlistToggle) {
        stopAction(event);
        openWishlist();
        return;
      }

      const wishlistClose = event.target.closest?.('[data-anushra-wishlist-close]');
      if (wishlistClose) {
        stopAction(event);
        closeWishlist();
        return;
      }

      const remove = event.target.closest?.('[data-anushra-wishlist-remove]');
      if (remove) {
        stopAction(event);
        const handle = remove.dataset.anushraWishlistRemove;
        writeList(STORAGE_WISHLIST, wishlistItems().filter((item) => item.handle !== handle));
        updateWishlistUI();
        return;
      }

      const searchTrigger = event.target.closest?.('[data-anushra-search-open]');
      if (searchTrigger) {
        stopAction(event);
        const nativeSearch = document.querySelector('.header__search summary');
        if (nativeSearch) nativeSearch.click();
        else window.location.assign('/search');
      }
    }, true);

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && wishlistDrawer() && !wishlistDrawer().hidden) closeWishlist();
    });
  };

  const setupMobileNavigation = () => {
    const path = window.location.pathname;
    document.querySelectorAll('[data-anushra-route]').forEach((item) => {
      const route = item.dataset.anushraRoute;
      const active =
        (route === 'shop' && (path.includes('/collections') || path.includes('/products'))) ||
        (route === 'search' && path.includes('/search')) ||
        (route === 'account' && path.includes('/account')) ||
        (route === 'cart' && path.includes('/cart'));
      item.classList.toggle('is-active', active);
      if (active) item.setAttribute('aria-current', 'page');
      else item.removeAttribute('aria-current');
    });
  };

  const setupDeliveryEstimator = (scope = document) => {
    scope.querySelectorAll('[data-anushra-delivery-form]').forEach((form) => {
      if (form.dataset.anushraReady === 'true') return;
      form.dataset.anushraReady = 'true';
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        const input = form.querySelector('input[name="pincode"]');
        const result = form.querySelector('[data-anushra-delivery-result]');
        const code = (input?.value || '').trim();
        if (!result) return;
        if (!/^\d{6}$/.test(code)) {
          result.textContent = 'Enter a valid 6-digit Indian pincode.';
          input?.focus();
          return;
        }
        result.textContent = 'Pincode format verified. Delivery availability and the final estimate are confirmed at checkout.';
      });
    });
  };

  const setupStickyCart = (scope = document) => {
    scope.querySelectorAll('[data-anushra-sticky-cart]').forEach((sticky) => {
      if (sticky.dataset.anushraReady === 'true') return;
      sticky.dataset.anushraReady = 'true';

      const mainButtonId = sticky.dataset.mainButtonId;
      const mainButton = mainButtonId ? document.getElementById(mainButtonId) : null;
      const stickyButton = sticky.querySelector('[data-anushra-sticky-submit]');
      const stickyPrice = sticky.querySelector('[data-anushra-sticky-price]');
      const priceSource = document.querySelector(sticky.dataset.priceTarget || '');
      if (!mainButton || !stickyButton) return;

      sticky.hidden = false;
      const syncState = () => {
        stickyButton.disabled = mainButton.disabled;
        stickyButton.setAttribute('aria-disabled', String(mainButton.disabled));
        const label = mainButton.querySelector('span')?.textContent?.trim();
        if (label && stickyButton.querySelector('span')) stickyButton.querySelector('span').textContent = label;
        const currentPrice = priceSource?.querySelector('.price-item--sale, .price-item--regular');
        if (stickyPrice && currentPrice?.textContent) stickyPrice.textContent = currentPrice.textContent.trim();
      };
      syncState();

      const stateObserver = new MutationObserver(syncState);
      stateObserver.observe(mainButton, { attributes: true, childList: true, subtree: true });
      if (priceSource) stateObserver.observe(priceSource, { childList: true, subtree: true, characterData: true });

      stickyButton.addEventListener('click', () => {
        if (mainButton.disabled) {
          document.querySelector('variant-selects, variant-radios, .product-form__input')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
        mainButton.click();
      });

      const setVisible = (visible) => {
        sticky.classList.toggle('is-visible', visible);
        document.body.classList.toggle('anushra-v100-sticky-cart-visible', visible);
      };

      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(([entry]) => setVisible(!entry.isIntersecting), { threshold: .15 });
        observer.observe(mainButton);
      } else {
        setVisible(true);
      }
    });
  };

  const improveFilterAccessibility = (scope = document) => {
    scope.querySelectorAll('.mobile-facets__open').forEach((button) => {
      if (!button.getAttribute('aria-label')) button.setAttribute('aria-label', 'Open product filters');
    });
  };

  const trackCurrentProduct = () => {
    const dataNode = document.getElementById('AnushraCurrentProduct');
    if (!dataNode) return;
    try {
      const item = JSON.parse(dataNode.textContent);
      if (!item?.handle) return;
      const list = readList(STORAGE_RECENT, STORAGE_RECENT_LEGACY).filter((entry) => entry.handle !== item.handle);
      list.unshift(item);
      writeList(STORAGE_RECENT, list.slice(0, MAX_RECENT));
    } catch (_) {}
  };

  const renderRecentlyViewed = (scope = document) => {
    const items = readList(STORAGE_RECENT, STORAGE_RECENT_LEGACY);
    scope.querySelectorAll('[data-anushra-recently-viewed-section]').forEach((section) => {
      const currentHandle = section.dataset.currentHandle || '';
      const visibleItems = items.filter((item) => item.handle && item.handle !== currentHandle).slice(0, 8);
      const track = section.querySelector('[data-anushra-recently-viewed-items]');
      if (!track || visibleItems.length === 0) {
        section.hidden = true;
        return;
      }
      track.innerHTML = visibleItems.map((item) => `
        <article class="anushra-recently-viewed__item" role="listitem">
          <div class="anushra-recently-viewed__card">
            <a href="${escapeHtml(item.url)}">
              <span class="anushra-recently-viewed__image">${item.image ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}" width="480" height="600" loading="lazy">` : ''}</span>
              <span class="anushra-recently-viewed__content"><strong>${escapeHtml(item.title)}</strong><span>${escapeHtml(item.price)}</span></span>
            </a>
          </div>
        </article>
      `).join('');
      section.hidden = false;
    });
  };

  const updateCartCounts = (count) => {
    if (typeof count !== 'number') return;
    document.querySelectorAll('[data-anushra-cart-count]').forEach((badge) => {
      badge.textContent = count;
      badge.hidden = count === 0;
    });
  };

  const setupCartSubscription = () => {
    if (cartSubscribed) return;
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      cartSubscribed = true;
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => updateCartCounts(event?.cartData?.item_count));
    }
  };

  const setupScrollState = () => {
    if (document.documentElement.dataset.anushraScrollReady === 'true') return;
    document.documentElement.dataset.anushraScrollReady = 'true';
    const update = () => document.documentElement.classList.toggle('anushra-page-scrolled', window.scrollY > 18);
    update();
    window.addEventListener('scroll', update, { passive: true });
  };

  const init = (scope = document) => {
    setupGlobalEvents();
    setupMobileNavigation();
    setupDeliveryEstimator(scope);
    setupStickyCart(scope);
    improveFilterAccessibility(scope);
    updateWishlistUI();
    renderRecentlyViewed(scope);
    setupCartSubscription();
    setupScrollState();
  };

  API.init = init;
  ready(() => {
    trackCurrentProduct();
    init(document);
  });

  document.addEventListener('shopify:section:load', (event) => init(event.target || document));
})();

